<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import DateRangeInput from 'dashboard/components-next/filter/inputs/DateRangeInput.vue';
import MonthInput from 'dashboard/components-next/filter/inputs/MonthInput.vue';
import { RELATIVE_DAYS_MAX } from 'dashboard/components-next/filter/helper/dateRangeOperators';
import {
  NO_VALUE_OPERATORS,
  OPERATORS_BY_TYPE,
} from 'kanban/helper/taskFilters';

// Uma condicao { attribute_key, filter_operator, values } sobre um atributo personalizado de cartao. Reutilizada
// pelo painel do funil e pelas specs 006/007.
const props = defineProps({
  // Definicoes task_attribute da conta (store `attributes`).
  definitions: { type: Array, required: true },
  // Chaves usadas em outras linhas: ficam desabilitadas aqui.
  usedKeys: { type: Array, default: () => [] },
});

const emit = defineEmits(['remove']);

const condition = defineModel({ type: Object, required: true });

const { t } = useI18n();

const definition = computed(() =>
  props.definitions.find(
    item => item.attribute_key === condition.value.attribute_key
  )
);
const type = computed(() => definition.value?.attribute_display_type);
const operator = computed(() => condition.value.filter_operator);
const values = computed(() => condition.value.values);

const attributeOptions = computed(() =>
  props.definitions.map(item => ({
    label: item.attribute_display_name,
    value: item.attribute_key,
    disabled:
      item.attribute_key !== condition.value.attribute_key &&
      props.usedKeys.includes(item.attribute_key),
  }))
);

const operatorLabel = name => {
  const isDate = type.value === 'date';
  const labels = {
    equal_to:
      type.value === 'list'
        ? t('KANBAN.FILTERS.OPERATORS.EQUAL_TO_ANY')
        : t('KANBAN.FILTERS.OPERATORS.EQUAL_TO'),
    contains: t('KANBAN.FILTERS.OPERATORS.CONTAINS'),
    is_greater_than: isDate
      ? t('KANBAN.FILTERS.OPERATORS.IS_AFTER')
      : t('KANBAN.FILTERS.OPERATORS.IS_GREATER_THAN'),
    is_less_than: isDate
      ? t('KANBAN.FILTERS.OPERATORS.IS_BEFORE')
      : t('KANBAN.FILTERS.OPERATORS.IS_LESS_THAN'),
    is_present: t('KANBAN.FILTERS.OPERATORS.IS_PRESENT'),
    is_not_present: t('KANBAN.FILTERS.OPERATORS.IS_NOT_PRESENT'),
    // Os quatro operadores de data por periodo usam os rotulos da 008 (filtro de conversas).
    between: isDate
      ? t('FILTER.OPERATOR_LABELS.between')
      : t('KANBAN.FILTERS.OPERATORS.BETWEEN_NUMBER'),
    in_month: t('FILTER.OPERATOR_LABELS.in_month'),
    next_days: t('FILTER.OPERATOR_LABELS.next_days'),
    last_days: t('FILTER.OPERATOR_LABELS.last_days'),
  };
  return labels[name];
};

const operatorOptions = computed(() =>
  (OPERATORS_BY_TYPE[type.value] || []).map(name => ({
    label: operatorLabel(name),
    value: name,
  }))
);

// O que a linha mostra para digitar o valor, por operador e tipo.
const inputKind = computed(() => {
  if (!type.value || NO_VALUE_OPERATORS.includes(operator.value)) return null;
  if (type.value === 'checkbox') return 'boolean';
  if (type.value === 'list') return 'list';
  if (type.value === 'text') return 'text';
  if (type.value === 'date') {
    if (operator.value === 'between') return 'dateRange';
    if (operator.value === 'in_month') return 'month';
    if (['next_days', 'last_days'].includes(operator.value)) return 'days';
    return 'date';
  }
  return operator.value === 'between' ? 'numberRange' : 'number';
});

const defaultValues = (attributeType, operatorName) =>
  attributeType === 'checkbox' && operatorName === 'equal_to' ? ['true'] : [];

const selectAttribute = key => {
  const attributeType = props.definitions.find(
    item => item.attribute_key === key
  )?.attribute_display_type;
  const firstOperator = OPERATORS_BY_TYPE[attributeType]?.[0];
  condition.value = {
    attribute_key: key,
    filter_operator: firstOperator,
    values: defaultValues(attributeType, firstOperator),
  };
};

const selectOperator = name => {
  condition.value = {
    ...condition.value,
    filter_operator: name,
    values: defaultValues(type.value, name),
  };
};

const setValue = (index, value) => {
  const next = [...values.value];
  next[index] = value === null || value === undefined ? '' : String(value);
  condition.value = { ...condition.value, values: next };
};

const setValues = next => {
  condition.value = { ...condition.value, values: next };
};

const selectListValues = items => {
  condition.value = {
    ...condition.value,
    values: (items || []).map(item => item.id),
  };
};

const singleInputType = computed(
  () => ({ date: 'date', number: 'number' })[inputKind.value] || 'text'
);

const booleanOptions = computed(() => [
  { label: t('KANBAN.FILTERS.ATTRIBUTES.TRUE'), value: 'true' },
  { label: t('KANBAN.FILTERS.ATTRIBUTES.FALSE'), value: 'false' },
]);

const listOptions = computed(() =>
  (definition.value?.attribute_values || []).map(value => ({
    id: value,
    name: value,
  }))
);
const listSelection = computed(() =>
  values.value.map(value => ({ id: value, name: value }))
);
</script>

<template>
  <li class="flex flex-wrap items-center gap-2 list-none">
    <FilterSelect
      :model-value="condition.attribute_key || ''"
      :options="attributeOptions"
      :label="
        definition
          ? definition.attribute_display_name
          : t('KANBAN.FILTERS.ATTRIBUTES.ATTRIBUTE_PLACEHOLDER')
      "
      variant="faded"
      class="shrink-0"
      @update:model-value="selectAttribute"
    />
    <FilterSelect
      v-if="type && type !== 'checkbox'"
      :model-value="operator"
      :options="operatorOptions"
      variant="ghost"
      class="shrink-0"
      @update:model-value="selectOperator"
    />
    <FilterSelect
      v-if="inputKind === 'boolean'"
      :model-value="values[0] || 'true'"
      :options="booleanOptions"
      variant="ghost"
      class="shrink-0"
      @update:model-value="setValue(0, $event)"
    />
    <MultiSelect
      v-else-if="inputKind === 'list'"
      :model-value="listSelection"
      :options="listOptions"
      dropdown-max-height="max-h-72"
      @update:model-value="selectListValues"
    />
    <DateRangeInput
      v-else-if="inputKind === 'dateRange'"
      :model-value="values"
      @update:model-value="setValues"
    />
    <MonthInput
      v-else-if="inputKind === 'month'"
      :model-value="values[0] || ''"
      @update:model-value="setValues($event ? [$event] : [])"
    />
    <template v-else-if="inputKind === 'numberRange'">
      <Input
        :model-value="values[0] || ''"
        type="number"
        size="sm"
        class="w-28"
        @update:model-value="setValue(0, $event)"
      />
      <Input
        :model-value="values[1] || ''"
        type="number"
        size="sm"
        class="w-28"
        @update:model-value="setValue(1, $event)"
      />
    </template>
    <Input
      v-else-if="inputKind === 'days'"
      :model-value="values[0] || ''"
      type="number"
      size="sm"
      class="w-24"
      :max="String(RELATIVE_DAYS_MAX)"
      @update:model-value="setValue(0, $event)"
    />
    <Input
      v-else-if="inputKind"
      :model-value="values[0] || ''"
      :type="singleInputType"
      :placeholder="t('KANBAN.FILTERS.ATTRIBUTES.VALUE_PLACEHOLDER')"
      size="sm"
      class="w-44"
      @update:model-value="setValue(0, $event)"
    />
    <Button
      icon="i-lucide-trash"
      variant="ghost"
      color="slate"
      size="sm"
      class="ms-auto"
      @click="emit('remove')"
    />
  </li>
</template>
